"use strict";
// Fuzzy matching of a free-text bank counterparty to a known Company. Used when
// Airwallex payer/beneficiary enrichment finds nothing and all we have is the
// raw transaction narrative, which embeds the company name amongst bank codes,
// references and amounts — and usually TRUNCATED (e.g. "LEADER METAL INTERNATION"
// for "LEADER METAL INTERNATIONAL TRADING"). An exact normalised match (see
// normalizeCompanyName / cash-events-company-linker) misses those, so this scores
// the overlap instead. Deliberately never auto-links: a fuzzy hit is a suggestion
// for a human to confirm, because mis-linking feeds the reconciliation engine a
// wrong company filter — worse than leaving the event unlinked.
Object.defineProperty(exports, "__esModule", { value: true });
exports.COMPANY_SUGGEST_MIN_SCORE = void 0;
exports.scoreNameMatch = scoreNameMatch;
exports.buildCompanyMatchIndex = buildCompanyMatchIndex;
exports.suggestCompany = suggestCompany;
const company_1 = require("./company");
// Score at/above which a fuzzy match is surfaced as a suggestion. A single
// generic shared token (e.g. two unrelated "NINGBO … CO" names) scores ~0.5 on a
// two-token name, so this floor keeps those out on its own.
exports.COMPANY_SUGGEST_MIN_SCORE = 0.6;
// If the best and second-best DISTINCT companies land within this margin the
// match is ambiguous and nothing is suggested — a near-tie is a coin-flip.
const AMBIGUITY_MARGIN = 0.08;
// Bank reference blocks that precede/follow the name but never contain it.
const REF_MARKER = /\b(?:RTGS|FOR\s+TT|TT)\b/;
// Airwallex 16-char transaction code that abuts the name with no separator.
const TXN_CODE = /HK5I[A-Z0-9]{12}/;
function tokenize(text) {
    const normalized = (0, company_1.normalizeCompanyName)(text);
    return normalized === '' ? [] : normalized.split(' ');
}
// Candidate tokens drop anything containing a digit: those are bank/reference
// codes, never part of a company name in this book, and keeping them would drag
// precision down on an otherwise-clean match.
function tokenizeCandidate(text) {
    return tokenize(text).filter((token) => !/\d/.test(token));
}
// Equality, or one token a prefix of the other (min length 4) — narratives
// truncate the name, so "internation" must still match "international".
function tokensMatch(a, b) {
    if (a === b)
        return true;
    if (Math.min(a.length, b.length) < 4)
        return false;
    return a.startsWith(b) || b.startsWith(a);
}
function countMatched(from, against) {
    return from.filter((token) => against.some((other) => tokensMatch(token, other))).length;
}
// Prefix-aware token-overlap F1. Precision guards against a long candidate that
// merely contains the company's tokens by chance; recall against a candidate
// that covers only part of the name. 0 when either side is empty or disjoint.
function scoreTokens(candidate, company) {
    if (candidate.length === 0 || company.length === 0)
        return 0;
    const recall = countMatched(company, candidate) / company.length;
    const precision = countMatched(candidate, company) / candidate.length;
    if (recall === 0 || precision === 0)
        return 0;
    return (2 * precision * recall) / (precision + recall);
}
function scoreNameMatch(candidate, companyName) {
    return scoreTokens(tokenizeCandidate(candidate), tokenize(companyName));
}
function cutAtRefMarker(raw) {
    const marker = REF_MARKER.exec(raw);
    return marker === null ? raw : raw.slice(0, marker.index);
}
// The several places a name hides in a narrative. Each returns the raw slice
// (still dirty); candidateTokenLists cleans and dedupes them. Ordering doesn't
// matter — the best-scoring candidate wins per company.
function nameSlices(narrative) {
    const slices = [narrative];
    const obo = /\/OBO\/([^/]+)/i.exec(narrative); // SWIFT "on behalf of"
    if (obo?.[1] !== undefined)
        slices.push(obo[1]);
    const received = /received from\s+(.+)/i.exec(narrative);
    if (received?.[1] !== undefined)
        slices.push(received[1]);
    const pipe = narrative.indexOf('|'); // Pattern C: name leads, bank details follow
    if (pipe > 0)
        slices.push(narrative.slice(0, pipe));
    const afterCode = new RegExp(`${TXN_CODE.source}(.+)`).exec(narrative);
    if (afterCode?.[1] !== undefined)
        slices.push(afterCode[1]);
    const afterRef = /(?:RTGS|TT)\|\S+\s+(.+)/.exec(narrative); // repeated name after the ref
    if (afterRef?.[1] !== undefined)
        slices.push(afterRef[1]);
    return slices;
}
function candidateTokenLists(narrative) {
    const lists = [];
    const seen = new Set();
    for (const slice of nameSlices(narrative)) {
        const tokens = tokenizeCandidate(cutAtRefMarker(slice));
        if (tokens.length === 0)
            continue;
        const key = tokens.join(' ');
        if (seen.has(key))
            continue;
        seen.add(key);
        lists.push(tokens);
    }
    return lists;
}
// Normalise + tokenise the company book once. Names that normalise to nothing
// (all-suffix / punctuation) are dropped.
function buildCompanyMatchIndex(companies) {
    const entries = [];
    for (const company of companies) {
        const tokens = tokenize(company.name);
        if (tokens.length === 0)
            continue;
        entries.push({ id: company.id, name: company.name, tokens });
    }
    return entries;
}
function bestCandidateScore(candidates, companyTokens) {
    let best = 0;
    for (const candidate of candidates) {
        const score = scoreTokens(candidate, companyTokens);
        if (score > best)
            best = score;
    }
    return best;
}
// Fold a fresh score into the running [best, second-best-distinct-company] pair.
function rankInto(ranked, match) {
    const [best, second] = ranked;
    if (best === null || match.score > best.score) {
        const demoted = best !== null && best.companyId !== match.companyId ? best : second;
        return [match, demoted];
    }
    if (match.companyId === best.companyId)
        return [best, second];
    if (second === null || match.score > second.score)
        return [best, match];
    return [best, second];
}
// Best confident company for a narrative, or null when nothing clears the score
// floor or the top two distinct companies are too close to call.
function suggestCompany(narrative, index) {
    const candidates = candidateTokenLists(narrative);
    if (candidates.length === 0)
        return null;
    let ranked = [null, null];
    for (const entry of index) {
        const score = bestCandidateScore(candidates, entry.tokens);
        if (score === 0)
            continue;
        ranked = rankInto(ranked, { companyId: entry.id, companyName: entry.name, score });
    }
    const [best, second] = ranked;
    if (best === null || best.score < exports.COMPANY_SUGGEST_MIN_SCORE)
        return null;
    if (second !== null && best.score - second.score < AMBIGUITY_MARGIN)
        return null;
    return best;
}
//# sourceMappingURL=company-match.js.map